import { Injectable } from '@angular/core';
import { BaseService } from '../../../../../../shared/src/lib/services/base.service';
import { map } from 'rxjs';
import { ISR } from '../../../../../../shared/src/lib/models/index.model';
import {
  IConfiguration,
  ISaveConfiguration,
  IConfigTask,
  IConfigInfraction,
  IConfigTaskSearchRequest,
  IConfigInfractionSearchRequest,
  IConfigLevySearchRequest,
  IConfigLevy,
  IConfigPortal,
} from './configuration.model';
import { uniq, uniqBy } from 'lodash-es';
import { IGetQuery } from 'ets-fe-ng-sdk';

/**
 * Service for managing configuration settings, tasks, and infractions.
 * Provides CRUD operations for configuration-related data.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigurationService extends BaseService {
  protected override baseURL = 'v1/config';

  /**
   * Fetches the last configuration version.
   * @returns Observable of the most recent configuration.
   */
  getLastVersion() {
    return this.fetchAll({ sortBy: 'createdOn', sortDirection: 'DESC' }).pipe(map((r) => r?.content?.[0]));
  }

  /**
   * Fetches all configurations with optional query parameters.
   * @param query - Optional query parameters for filtering.
   * @returns Observable of paginated configuration results.
   */
  fetchAll(query?: IGetQuery<IConfiguration>) {
    return this.get<ISR<IConfiguration>>('', query).pipe(
      map((r) => {
        r?.content?.forEach(
          (x) => (x._naicomEmailList = x.naicomEmailList?.split(',').map((d) => ({ email: d.trim() })) || []),
        );
        return r;
      }),
    );
  }

  /**
   * Saves a configuration (create or update).
   * @param data - The configuration data to save.
   * @returns Observable of the saved configuration.
   */
  save({ _naicomEmailList, ...data }: ISaveConfiguration) {
    const duplicateMapCheck: { [email: string]: boolean } = {};
    data.naicomEmailList = '';
    for (const { email } of _naicomEmailList || []) {
      const _email = email?.trim();
      if (_email?.length > 0 && !duplicateMapCheck[_email]) data.naicomEmailList += ',' + _email;
    }
    data.naicomEmailList = data.naicomEmailList.length ? data.naicomEmailList.slice(1) : undefined;
    return data.id ? this.put<IConfiguration>(data) : this.post<IConfiguration>(data);
  }

  // ============ Config Tasks ============

  /**
   * Searches for configuration tasks.
   * Endpoint: GET /api/v1/config/task/search
   * @param query - Query parameters for searching tasks.
   * @returns Observable of paginated task results.
   */
  searchTasks(query?: IGetQuery<IConfigTaskSearchRequest>) {
    return this.get<ISR<IConfigTask>>('/task/search', query);
  }

  /**
   * Creates a new configuration task.
   * Endpoint: POST /api/v1/config/task
   * @param task - The task data to create.
   * @returns Observable of the created task.
   */
  createTask(task: Partial<IConfigTask>) {
    return this.post<IConfigTask>(task, '/task');
  }

  /**
   * Updates an existing configuration task.
   * Endpoint: PUT /api/v1/config/task/{id}
   * @param task - The task data to update (must include id).
   * @returns Observable of the updated task.
   */
  updateTask(task: Partial<IConfigTask>) {
    if (!task.id) {
      throw new Error('Task ID is required for update');
    }
    return this.put<IConfigTask>(task, `/task/${task.id}`);
  }

  /**
   * Deletes a configuration task.
   * @param id - The ID of the task to delete.
   * @returns Observable of the delete response.
   */
  deleteTask(id: number) {
    return this.delete(`/task/${id}`);
  }

  // ============ Config Infractions ============

  /**
   * Searches for configuration infractions.
   * Endpoint: GET /api/v1/config/infractions/search
   * @param query - Query parameters for searching infractions.
   * @returns Observable of paginated infraction results.
   */
  searchInfractions(query?: IGetQuery<IConfigInfractionSearchRequest>) {
    return this.get<ISR<IConfigInfraction>>('/infractions/search', query);
  }

  /**
   * Creates a new configuration infraction.
   * Endpoint: POST /api/v1/config/infractions
   * @param infraction - The infraction data to create.
   * @returns Observable of the created infraction.
   */
  createInfraction(infraction: Partial<IConfigInfraction>) {
    return this.post<IConfigInfraction>(infraction, '/infractions');
  }

  /**
   * Updates an existing configuration infraction.
   * Endpoint: PUT /api/v1/config/infractions/{id}
   * @param infraction - The infraction data to update (must include id).
   * @returns Observable of the updated infraction.
   */
  updateInfraction(infraction: Partial<IConfigInfraction>) {
    if (!infraction.id) {
      throw new Error('Infraction ID is required for update');
    }
    return this.put<IConfigInfraction>(infraction, `/infractions/${infraction.id}`);
  }

  /**
   * Deletes a configuration infraction.
   * @param id - The ID of the infraction to delete.
   * @returns Observable of the delete response.
   */
  deleteInfraction(id: number) {
    return this.delete(`/infractions/${id}`);
  }

  // ============ Config Levies ============

  /**
   * Searches for configuration levies.
   * Endpoint: GET /api/v1/config/levies/search
   * @param query - Query parameters for searching levies.
   * @returns Observable of paginated levy results.
   */
  searchLevy(query?: IGetQuery<IConfigLevySearchRequest>) {
    return this.get<ISR<IConfigLevy>>('/levies/search', query);
  }

  /**
   * Creates a new configuration levy.
   * Endpoint: POST /api/v1/config/levies
   * @param levy - The levy data to create.
   * @returns Observable of the created levy.
   */
  createLevy(levy: Partial<IConfigLevy>) {
    return this.post<IConfigLevy>(levy, '/levies');
  }

  /**
   * Updates an existing configuration levy.
   * Endpoint: PUT /api/v1/config/levies/{id}
   * @param levy - The levy data to update (must include id).
   * @returns Observable of the updated levy.
   */
  updateLevy(levy: Partial<IConfigLevy>) {
    if (!levy.id) {
      throw new Error('Levy ID is required for update');
    }
    return this.put<IConfigLevy>(levy, `/levies/${levy.id}`);
  }

  /**
   * Deletes a configuration levy.
   * @param id - The ID of the levy to delete.
   * @returns Observable of the delete response.
   */
  deleteLevy(id: number) {
    return this.delete(`/levies/${id}`);
  }

  // ============ Config Portals ============
  /**
   * Searches for configuration portals.
   * @param query - Query parameters for searching portals.
   * @returns Observable of paginated portal results.
   */
  searchPortals(query?: any) {
    return this.get<IConfigPortal[]>('/portal');
  }

  /**
   * Creates a new configuration portal.
   * @param portal - The portal data to create.
   * @returns Observable of the created portal.
   */
  createPortal(portal: Partial<IConfigPortal>) {
    return this.post<IConfigPortal>(portal, '/portal');
  }

  /**
   * Updates an existing configuration portal.
   * @param id - The ID of the portal to update.
   * @param portal - The portal data to update.
   * @returns Observable of the updated portal.
   */
  updatePortal(id: string | number, portal: Partial<IConfigPortal>) {
    return this.put<IConfigPortal>(portal, `/portal/${portal.id}`);
  }

  /**
   * Deletes a configuration portal.
   * @param id - The ID of the portal to delete.
   * @returns Observable of the delete response.
   */
  deletePortals(id: number) {
    return this.delete(`/portal/${id}`);
  }

  /**
   * Fetches all configuration portals.
   * @returns Observable of all configuration portals.
   */
  fetchAllPortals() {
    return this.get<IConfigPortal[]>('/portal');
  }
}
